/**
 * Campaign Report — data layer + metrics
 *
 * Powers the "Campaign Reports" tab on /admin/analytics (Reporting &
 * Analytics). Unlike deliveryReport.ts — which reports a SINGLE campaign's
 * deliveries — this assembles a CROSS-CAMPAIGN report for the current org,
 * spanning every campaign the org:
 *   - SENT      (source campaigns it owns — no copy flags)
 *   - SHARED    (studio / agency copies — isCampaignCopy + copyRole)
 *   - DELIVERED (broadcaster / media-house copies — isDeliveredJob)
 *
 * Data all lives under the org's own `organizations/{orgId}/jobs` collection
 * (relationships are distinguished by flags), so no cross-org reads are
 * needed for the core report. QC / review headline metrics come from the
 * org's tracks store (audiometa copies carry synced qc_status /
 * approval_status / fileVersion / qcVersion). Optional deep QC / review
 * history reads the per-track subcollections.
 *
 * This module is the pure data/metrics core. PDF / Excel / CSV generators
 * consume the assembled `CampaignReport` object (see campaignReportExport.ts).
 */

import { collection, collectionGroup, getDocs, query, doc, getDoc } from 'firebase/firestore';
import { getOrgCollectionPath } from '../constants/paths';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Relationship = 'sent' | 'shared' | 'delivered';

export interface DateRange {
  /** Inclusive start (ms). null = unbounded. */
  start: number | null;
  /** Exclusive-ish end (ms). null = unbounded. */
  end: number | null;
  /** Preset key used to build this range (for display + trend bucketing). */
  preset: DateRangePreset;
  label: string;
}

export type DateRangePreset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'year_to_date'
  | 'last_12_months'
  | 'all_time'
  | 'custom';

export interface CampaignReportFilters {
  dateRange: DateRange;
  /** Selected client names (case-insensitive match). Empty = all clients. */
  clients: string[];
  /** When true, read per-track qcRecords / reviews subcollections for full
   *  historical failure / rejection counts. Slower. */
  includeQcReviewHistory: boolean;
}

/** Per-campaign computed row. */
export interface CampaignRow {
  jobId: string;
  relationship: Relationship;
  client: string;
  campaign: string;
  period: string;
  /** Other party: for sent = '' (we are the source); for copies = source org. */
  sourceOrgName: string;
  copyRole?: string;
  /** Anchor date (delivery date) in ms. */
  deliveryDate: number | null;

  // Volume
  stationCount: number;
  spotCount: number;       // distinct flighting codes
  trackCount: number;      // distinct linked tracks
  liveReadCount: number;
  mediaOwnerCount: number;
  /** Distinct flighting codes on this campaign (already in the stations
   *  array — no extra reads). Shown in the per-campaign expand row. */
  flightingCodes: string[];

  // Delivery / engagement
  deliveryRecords: number;   // # delivery records (sent only; else derived)
  accepted: number;
  rejected: number;
  pending: number;
  downloadsComplete: number;
  downloadsTotal: number;
  ftpSends: number;

  // QC (headline, from linked tracks)
  qcPass: number;
  qcFail: number;
  qcPendingCount: number;
  avgQcAttempts: number;

  // Review (headline, from linked tracks)
  approved: number;
  reviewRejected: number;
  reviewPending: number;
  avgRevisions: number;

  // Deep history (optional)
  qcTestsTotal?: number;
  qcFailuresTotal?: number;
  reviewsTotal?: number;
  reviewRejectionsTotal?: number;
  rejectionReasons?: string[];

  // Breakdowns (for chart aggregation)
  languages: Record<string, number>;   // language -> station count
  durations: Record<string, number>;   // duration -> spot count
  mediaOwners: Record<string, number>; // media owner -> station count

  // Per-delivery timestamps (for the trend line)
  deliveryTimestamps: number[];
  spotTimestamps: number[];

  // Linked track ids (for optional deep QC / review history) + the org
  // whose audiometa subcollections hold that history (source for copies).
  linkedTrackIds: string[];
  historyOrgId: string;

  // Per-broadcaster download activity on this campaign (for the "who is /
  // isn't downloading" breakdown).
  broadcasters: BroadcasterActivity[];
}

/** One broadcaster's download state on a single campaign. */
export interface BroadcasterActivity {
  key: string;            // orgId when known, else lower-cased name
  name: string;
  isMediaHouse: boolean;
  /** A download-status signal exists for this broadcaster (i.e. delivered). */
  hasStatus: boolean;
  downloaded: boolean;    // status === 'complete'
  partial: boolean;       // status === 'partial'
  lastDownloadedAt: number | null;
}

/** Aggregated across every campaign in the report — one row per broadcaster. */
export interface BroadcasterStat {
  name: string;
  isMediaHouse: boolean;
  campaigns: number;      // campaigns this broadcaster appears on
  deliveries: number;     // delivery-equivalents (one per campaign appearance)
  downloaded: number;     // fully downloaded
  partial: number;        // started but not complete
  outstanding: number;    // delivered, nothing pulled
  downloadRate: number;   // downloaded / deliveries
  lastDownloadedAt: number | null;
}

export interface SectionSummary {
  relationship: Relationship;
  label: string;
  campaigns: number;
  stations: number;
  spots: number;
  tracks: number;
  liveReads: number;
  deliveryRecords: number;
  accepted: number;
  rejected: number;
  pending: number;
  downloadsComplete: number;
  downloadsTotal: number;
  ftpSends: number;
  qcPass: number;
  qcFail: number;
  qcPendingCount: number;
  approved: number;
  reviewRejected: number;
  reviewPending: number;
  qcTestsTotal: number;
  qcFailuresTotal: number;
  reviewsTotal: number;
  reviewRejectionsTotal: number;
  rows: CampaignRow[];
}

export interface TrendPoint {
  /** Bucket key, e.g. '2026-08' or '2026-08-25'. */
  key: string;
  label: string;
  deliveries: number;
  spots: number;
  campaigns: number;
}

export interface CampaignReport {
  orgName: string;
  filters: CampaignReportFilters;
  generatedAt: number;

  /** Only sections with data are present. */
  sections: SectionSummary[];

  // Headline KPIs (aggregated across all included sections)
  headline: {
    totalCampaigns: number;
    totalStations: number;
    totalSpots: number;
    totalTracks: number;
    totalLiveReads: number;
    totalDeliveryRecords: number;
    downloadsComplete: number;
    downloadsTotal: number;
    downloadRate: number;      // 0..1
    accepted: number;
    rejected: number;
    pending: number;
    acceptRate: number;        // 0..1 (of accepted+rejected)
    qcPass: number;
    qcFail: number;
    qcFailRate: number;        // 0..1
    approved: number;
    reviewRejected: number;
    reviewRejectRate: number;  // 0..1
    ftpSends: number;
  };

  // Chart datasets
  charts: {
    relationshipSplit: { name: string; value: number }[];
    deliveryStatusSplit: { name: string; value: number }[];
    downloadEngagement: { name: string; value: number }[];
    languageSpread: { name: string; value: number }[];
    durationSpread: { name: string; value: number }[];
    campaignsPerClient: { name: string; value: number }[];
    downloadsByClient: { name: string; complete: number; outstanding: number }[];
    qcSplit: { name: string; value: number }[];
    reviewSplit: { name: string; value: number }[];
    downloadsByBroadcaster: { name: string; complete: number; outstanding: number }[];
    trend: TrendPoint[];
  };

  /** One row per broadcaster — who is / isn't downloading. */
  broadcasters: BroadcasterStat[];

  /** Distinct client names found (for the filter UI to offer). */
  availableClients: string[];
}

// ---------------------------------------------------------------------------
// Date range presets
// ---------------------------------------------------------------------------

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export const PRESET_LABELS: Record<DateRangePreset, string> = {
  this_month: 'This month',
  last_month: 'Last month',
  this_quarter: 'This quarter',
  year_to_date: 'Year to date',
  last_12_months: 'Last 12 months',
  all_time: 'All time',
  custom: 'Custom range',
};

/** Build a DateRange from a preset. `now` defaults to current time. */
export function buildDateRange(preset: DateRangePreset, now: Date = new Date()): DateRange {
  const y = now.getFullYear();
  const m = now.getMonth();
  let start: number | null = null;
  let end: number | null = null;

  switch (preset) {
    case 'this_month':
      start = new Date(y, m, 1).getTime();
      end = new Date(y, m + 1, 1).getTime();
      break;
    case 'last_month':
      start = new Date(y, m - 1, 1).getTime();
      end = new Date(y, m, 1).getTime();
      break;
    case 'this_quarter': {
      const qStartMonth = Math.floor(m / 3) * 3;
      start = new Date(y, qStartMonth, 1).getTime();
      end = new Date(y, qStartMonth + 3, 1).getTime();
      break;
    }
    case 'year_to_date':
      start = new Date(y, 0, 1).getTime();
      end = new Date(y + 1, 0, 1).getTime();
      break;
    case 'last_12_months':
      start = new Date(y, m - 11, 1).getTime();
      end = new Date(y, m + 1, 1).getTime();
      break;
    case 'all_time':
      start = null;
      end = null;
      break;
    case 'custom':
      start = null;
      end = null;
      break;
  }
  return { start, end, preset, label: PRESET_LABELS[preset] };
}

/** Build a custom DateRange from two Date objects (inclusive of both days). */
export function buildCustomRange(from: Date, to: Date): DateRange {
  const start = startOfDay(from);
  // Make `to` inclusive of the whole day by pushing end to next-day start.
  const end = startOfDay(to) + 24 * 60 * 60 * 1000;
  const fmt = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start, end, preset: 'custom', label: `${fmt(from)} → ${fmt(to)}` };
}

const inRange = (ts: number | null, range: DateRange): boolean => {
  if (ts == null) return false;
  if (range.start != null && ts < range.start) return false;
  if (range.end != null && ts >= range.end) return false;
  return true;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const clientName = (client: any): string => {
  if (!client) return '';
  if (typeof client === 'string') return client;
  return client.label || client.value || '';
};

const relationshipOf = (job: any): Relationship => {
  if (job?.isDeliveredJob) return 'delivered';
  if (job?.isCampaignCopy) return 'shared';
  return 'sent';
};

const RELATIONSHIP_LABELS: Record<Relationship, string> = {
  sent: 'Sent',
  shared: 'Shared with me',
  delivered: 'Delivered to me',
};

const isLiveStation = (s: any): boolean => !s?.deleted;
const isLiveDelivery = (d: any): boolean => d?.status !== 'deleted';

const languageLabel = (s: any): string => {
  if (s?.language && typeof s.language === 'string') return s.language;
  if (Array.isArray(s?.language)) {
    return s.language.map((l: any) => (typeof l === 'string' ? l : l?.label)).filter(Boolean).join(', ');
  }
  if (Array.isArray(s?.languageOptions)) {
    return s.languageOptions.map((o: any) => o?.label).filter(Boolean).join(', ');
  }
  return '';
};

const bump = (map: Record<string, number>, key: string, by = 1) => {
  const k = (key || '').trim();
  if (!k) return;
  map[k] = (map[k] || 0) + by;
};

// ---------------------------------------------------------------------------
// Broadcaster exclusions
// ---------------------------------------------------------------------------
// Some recipients receive spots but never download them through the platform
// (e.g. ZAPOP / Radio Retail, which redistributes internally). Counting them
// drags the download rate down and misrepresents broadcaster engagement, so
// they're excluded from DELIVERY + DOWNLOAD reporting: omitted from the
// broadcaster breakdown, and subtracted from the delivered / download counts.
// They do NOT affect content metrics (spots, placements, QC, review).
//
// Matched case-insensitively as a SUBSTRING of the broadcaster / station org
// name, so aliases like "ZAPOP / Radio Retail" all match. Edit the list to
// add/remove exclusions; set the flag to false to disable entirely.
export const EXCLUDE_NONDOWNLOADING_BROADCASTERS = true;
// NOTE: ZAPOP is intentionally NOT excluded — ZAPOP is a real recipient that
// actually downloads, so its metrics are genuine. Only Radio Retail (which is
// delivered link-only and never downloads) is filtered out. See
// docs/Radio-Retail-ZAPOP-Setup.md for the full picture and the future plan to
// make Radio Retail a real broadcaster under ZAPOP (which would let us drop
// this exclusion entirely).
export const EXCLUDED_BROADCASTER_MATCHERS = ['radio retail'];
/** Human-readable label for the exclusion note shown in the report UI. */
export const EXCLUDED_BROADCASTER_LABEL = 'Radio Retail';
export function isExcludedBroadcaster(name: string | null | undefined): boolean {
  if (!EXCLUDE_NONDOWNLOADING_BROADCASTERS) return false;
  const n = (name || '').toLowerCase();
  return EXCLUDED_BROADCASTER_MATCHERS.some((m) => n.includes(m));
}

// ---------------------------------------------------------------------------
// Per-campaign computation
// ---------------------------------------------------------------------------

interface ComputeInputs {
  job: any;
  deliveries: any[] | null; // sent only; null when not fetched
  tracksById: Record<string, any>;
  /** The org running the report (owns the jobs collection). */
  orgId: string;
  /** Authoritative download fields fetched from the SOURCE job for copy
   *  campaigns. The download CF reliably syncs the aggregate counter to
   *  copies but NOT the per-broadcaster broadcasterDownloadStatus map — the
   *  copy's map is typically empty. When provided, these override the copy's
   *  own (stale/empty) download fields so a copy's report mirrors the
   *  source's. Undefined for source (sent) campaigns — they use their own. */
  downloadOverride?: { deliveryCount?: any; downloadedDeliveryCount?: any; broadcasterDownloadStatus?: Record<string, string> };
  /** The SOURCE org's delivery records, for copy campaigns — carries the
   *  accept/reject/FTP data that copies don't hold themselves. */
  sourceDeliveries?: any[];
}

function computeCampaignRow({ job, deliveries, tracksById, orgId, downloadOverride, sourceDeliveries }: ComputeInputs): CampaignRow {
  const relationship = relationshipOf(job);
  const stations: any[] = Array.isArray(job.stations) ? job.stations.filter(isLiveStation) : [];

  // Volume
  const flightCodes = new Set<string>();
  const trackIds = new Set<string>();
  const mediaOwners: Record<string, number> = {};
  const languages: Record<string, number> = {};
  const durations: Record<string, number> = {};
  let liveReadCount = 0;

  for (const s of stations) {
    if (s.flightingCode) flightCodes.add(String(s.flightingCode).trim());
    if (s.linkedTrackId) trackIds.add(s.linkedTrackId);
    if (s.isLiveRead) liveReadCount++;
    bump(mediaOwners, s.mediaOwner);
    bump(languages, languageLabel(s));
    if (s.duration) bump(durations, String(s.duration));
  }

  const spotCount = flightCodes.size || trackIds.size;
  const trackCount = trackIds.size;

  // Delivery / engagement + delivery-date anchor.
  //
  // Response records (accept / reject / delivered + FTP + the Deliveries
  // count) come from the authoritative deliveries sub-collection: the org's
  // OWN deliveries for a sent campaign, the SOURCE org's for a copy (copies
  // don't hold their own). Media-house bundle deliveries are EXCLUDED — they
  // duplicate their child stations' deliveries, so counting them would
  // inflate the totals and make a source report disagree with a copy report
  // of the same campaign. `pending` = 'delivered' with no explicit
  // accept/reject (the default in the auto-delivery workflow).
  const liveDeliveries = Array.isArray(deliveries) ? deliveries.filter(isLiveDelivery) : [];
  const sourceLive = Array.isArray(sourceDeliveries) ? sourceDeliveries.filter(isLiveDelivery) : [];
  const allNonMh = (relationship === 'sent' ? liveDeliveries : sourceLive).filter((d) => !d.isMediaHouse);
  // Split off excluded recipients (e.g. ZAPOP / Radio Retail) — they don't
  // count toward deliveries, downloads, or the breakdown.
  const responseDeliveries = allNonMh.filter((d) => !isExcludedBroadcaster(d.broadcasterOrgName));
  const excludedDeliveries = allNonMh.filter((d) => isExcludedBroadcaster(d.broadcasterOrgName));

  // Station orgs to exclude from the per-broadcaster breakdown + the map-based
  // download fallback — from both the stations array and the excluded
  // deliveries (in case a station name differs from its delivery name).
  const excludedOrgIds = new Set<string>();
  for (const s of stations) {
    if (s.stationOrgId && isExcludedBroadcaster(s.stationOrgName || s.station)) excludedOrgIds.add(s.stationOrgId);
  }
  for (const d of excludedDeliveries) {
    if (d.broadcasterOrgId) excludedOrgIds.add(d.broadcasterOrgId);
  }

  let accepted = 0;
  let rejected = 0;
  let pending = 0;
  let ftpSends = 0;
  let deliveryDate: number | null = null;

  for (const d of responseDeliveries) {
    if (d.status === 'accepted') accepted++;
    else if (d.status === 'rejected') rejected++;
    else pending++;
    if (d.ftpStatus) ftpSends += d.ftpFileCount || 1;
  }

  if (relationship === 'sent') {
    for (const d of responseDeliveries) {
      const ts = d.deliveredAt || d.createdAt || null;
      if (ts && (deliveryDate == null || ts < deliveryDate)) deliveryDate = ts;
    }
  } else {
    // Copy: anchor on the copy's own delivered date (keeps the date filter
    // stable); accept/reject/FTP already read from the source above.
    deliveryDate = job.deliveredAt || job.uploadedAt || job.copyUpdatedAt || null;
  }

  // Download fields — for a copy campaign these come from the SOURCE job
  // (downloadOverride) because the copy's own broadcasterDownloadStatus map
  // is typically empty (the CF syncs the counter but not the map). For a
  // source/sent campaign, downloadOverride is undefined and we use the job's
  // own fields. Either way, source and copy reports of the same campaign now
  // read the same authoritative numbers.
  const dl = downloadOverride || job;
  const dlMap: Record<string, string> = (dl.broadcasterDownloadStatus || {}) as Record<string, string>;

  let downloadsTotal =
    typeof dl.deliveryCount === 'number' ? dl.deliveryCount : 0;
  let downloadsComplete =
    typeof dl.downloadedDeliveryCount === 'number' ? dl.downloadedDeliveryCount : 0;
  if (downloadsTotal === 0) {
    // Legacy / never-delivered campaigns without the badge fields: derive
    // from the per-broadcaster status map + stations (excluding excluded orgs).
    const orgIds = Array.from(new Set(stations.map((s: any) => s.stationOrgId).filter(Boolean)))
      .filter((id) => !excludedOrgIds.has(id as string));
    downloadsTotal = orgIds.length;
    downloadsComplete = orgIds.filter((id) => dlMap[id as string] === 'complete').length;
  } else {
    // Subtract excluded recipients from the synced badge aggregate so the
    // download rate reflects only broadcasters we actually track. Only
    // deliveries WITH a broadcasterOrgId are in the counter — deliveryCount
    // counts non-MH station deliveries that have an org, so a link-only
    // recipient (e.g. a routing-only Radio Retail with no stationOrgId) isn't
    // in it and must NOT be subtracted, or we'd under-count the denominator.
    for (const d of excludedDeliveries) {
      if (!d.broadcasterOrgId) continue;
      downloadsTotal = Math.max(0, downloadsTotal - 1);
      if (d.downloadStatus === 'complete') downloadsComplete = Math.max(0, downloadsComplete - 1);
    }
  }

  // One delivery record per non-MH recipient — the SAME set the
  // accept/reject/delivered counts run over, so delivered + accepted +
  // rejected === deliveryRecords, and a source report and a copy report of
  // the same campaign agree. Falls back to the synced download denominator
  // only when a copy's source deliveries couldn't be read.
  const deliveryRecords = responseDeliveries.length || downloadsTotal;

  // Trend timestamps — anchor the campaign at its delivery date and add
  // `deliveryRecords` to the deliveries line + `spotCount` to the spots line.
  // A campaign's deliveries are created in one batch (same deliveredAt), so
  // this is accurate, and it makes the trend lines sum to the Deliveries /
  // Spots headline KPIs — identically for sent and copy campaigns (previously
  // sent counted per-delivery while copies counted per-campaign, so the trend
  // disagreed with the counter).
  const deliveryTimestamps: number[] = [];
  const spotTimestamps: number[] = [];
  if (deliveryDate != null) {
    for (let i = 0; i < deliveryRecords; i++) deliveryTimestamps.push(deliveryDate);
    for (let i = 0; i < spotCount; i++) spotTimestamps.push(deliveryDate);
  }

  // QC / review headline — from the org's linked-track audiometa copies.
  let qcPass = 0;
  let qcFail = 0;
  let qcPendingCount = 0;
  let approved = 0;
  let reviewRejected = 0;
  let reviewPending = 0;
  let qcAttemptsSum = 0;
  let revisionsSum = 0;
  let trackWithMeta = 0;

  for (const tid of Array.from(trackIds)) {
    const t = tracksById[tid];
    if (!t) continue;
    trackWithMeta++;
    if (t.qc_status === 'pass') qcPass++;
    else if (t.qc_status === 'fail') qcFail++;
    else qcPendingCount++;
    if (t.approval_status === 'approved') approved++;
    else if (t.approval_status === 'rejected') reviewRejected++;
    else reviewPending++;
    qcAttemptsSum += Number(t.qcVersion) || 0;
    revisionsSum += Number(t.fileVersion) || 1;
  }
  const avgQcAttempts = trackWithMeta > 0 ? qcAttemptsSum / trackWithMeta : 0;
  const avgRevisions = trackWithMeta > 0 ? revisionsSum / trackWithMeta : 0;

  // Per-broadcaster download activity — derived from the SAME synced signal
  // the JobDetail per-station download badges use: the
  // broadcasterDownloadStatus map keyed by each station's broadcaster org
  // (stationOrgId). Using the map for EVERY section (not the source-only
  // deliveries sub-collection) means a source report and an agency report
  // of the same campaign show the same per-broadcaster breakdown. Station-
  // level by construction: media houses (mediaOwnerOrgId, not stationOrgId)
  // and link-only recipients (no stationOrgId) are excluded, matching the
  // download aggregate above.
  const broadcasters: BroadcasterActivity[] = [];
  const byOrg = new Map<string, { name: string }>();
  for (const s of stations) {
    const orgId = s.stationOrgId;
    if (!orgId || excludedOrgIds.has(orgId)) continue; // omit excluded recipients
    if (!byOrg.has(orgId)) byOrg.set(orgId, { name: s.stationOrgName || s.station || 'Unknown' });
  }
  // Exact last-download timestamps live only on the source's delivery
  // records — attach them when available (source view). The download counts
  // don't depend on this, so an agency view (null) still mirrors the counts.
  const lastDlByOrg = new Map<string, number>();
  if (relationship === 'sent' && Array.isArray(deliveries)) {
    for (const d of liveDeliveries) {
      if (d.isMediaHouse || !d.broadcasterOrgId) continue;
      const ts = d.lastDownloadedAt || d.downloadedAt || 0;
      if (ts) lastDlByOrg.set(d.broadcasterOrgId, Math.max(lastDlByOrg.get(d.broadcasterOrgId) || 0, ts));
    }
  }
  byOrg.forEach((info, orgId) => {
    const status = dlMap[orgId];
    broadcasters.push({
      key: orgId,
      name: info.name,
      isMediaHouse: false,
      hasStatus: true,
      downloaded: status === 'complete',
      partial: status === 'partial',
      lastDownloadedAt: lastDlByOrg.get(orgId) || null,
    });
  });

  return {
    jobId: job.id,
    relationship,
    client: clientName(job.client),
    campaign: job.campaign || '',
    period: job.period || '',
    sourceOrgName: relationship === 'sent' ? '' : job.sourceOrgName || '',
    copyRole: job.copyRole,
    deliveryDate,
    stationCount: stations.length,
    spotCount,
    trackCount,
    liveReadCount,
    mediaOwnerCount: Object.keys(mediaOwners).length,
    flightingCodes: Array.from(flightCodes).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })),
    deliveryRecords,
    accepted,
    rejected,
    pending,
    downloadsComplete,
    downloadsTotal,
    ftpSends,
    qcPass,
    qcFail,
    qcPendingCount,
    avgQcAttempts,
    approved,
    reviewRejected,
    reviewPending,
    avgRevisions,
    languages,
    durations,
    mediaOwners,
    deliveryTimestamps,
    spotTimestamps,
    linkedTrackIds: Array.from(trackIds),
    historyOrgId: relationship === 'sent' ? orgId : (job.sourceOrgId || orgId),
    broadcasters,
  };
}

/** Aggregate per-campaign broadcaster activity into one row per broadcaster. */
function buildBroadcasterBreakdown(rows: CampaignRow[]): BroadcasterStat[] {
  const byKey = new Map<string, BroadcasterStat & { _campaignKeys: Set<string> }>();
  for (const r of rows) {
    for (const b of r.broadcasters) {
      let stat = byKey.get(b.key);
      if (!stat) {
        stat = {
          name: b.name,
          isMediaHouse: b.isMediaHouse,
          campaigns: 0,
          deliveries: 0,
          downloaded: 0,
          partial: 0,
          outstanding: 0,
          downloadRate: 0,
          lastDownloadedAt: null,
          _campaignKeys: new Set<string>(),
        };
        byKey.set(b.key, stat);
      }
      stat.deliveries++;
      stat._campaignKeys.add(r.jobId);
      if (b.downloaded) stat.downloaded++;
      else if (b.partial) stat.partial++;
      else stat.outstanding++;
      if (b.lastDownloadedAt && (!stat.lastDownloadedAt || b.lastDownloadedAt > stat.lastDownloadedAt)) {
        stat.lastDownloadedAt = b.lastDownloadedAt;
      }
      // Prefer a media-house label if any appearance flags it.
      if (b.isMediaHouse) stat.isMediaHouse = true;
    }
  }
  const out = Array.from(byKey.values()).map((s) => {
    s.campaigns = s._campaignKeys.size;
    s.downloadRate = s.deliveries > 0 ? s.downloaded / s.deliveries : 0;
    const { _campaignKeys, ...rest } = s;
    return rest as BroadcasterStat;
  });
  // Surface the laggards first: most outstanding, then lowest download rate.
  out.sort((a, b) => (b.outstanding - a.outstanding) || (a.downloadRate - b.downloadRate) || a.name.localeCompare(b.name));
  return out;
}

// ---------------------------------------------------------------------------
// Trend bucketing
// ---------------------------------------------------------------------------

/** Decide month vs day granularity from the range span. */
function trendGranularity(range: DateRange, timestamps: number[]): 'month' | 'day' {
  let span = 0;
  if (range.start != null && range.end != null) {
    span = range.end - range.start;
  } else if (timestamps.length > 0) {
    span = Math.max(...timestamps) - Math.min(...timestamps);
  }
  const DAY = 24 * 60 * 60 * 1000;
  return span > 62 * DAY ? 'month' : 'day';
}

function bucketKey(ts: number, gran: 'month' | 'day'): { key: string; label: string } {
  const d = new Date(ts);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  if (gran === 'month') {
    const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return { key: `${d.getFullYear()}-${mm}`, label: `${MONTHS[d.getMonth()]} ${d.getFullYear()}` };
  }
  return { key: `${d.getFullYear()}-${mm}-${dd}`, label: `${dd}/${mm}` };
}

function buildTrend(rows: CampaignRow[], range: DateRange): TrendPoint[] {
  const allTs = rows.flatMap((r) => r.deliveryTimestamps);
  const gran = trendGranularity(range, allTs);
  const buckets = new Map<string, TrendPoint>();

  const add = (ts: number, field: 'deliveries' | 'spots' | 'campaigns') => {
    if (!inRange(ts, range) && (range.start != null || range.end != null)) return;
    const { key, label } = bucketKey(ts, gran);
    let pt = buckets.get(key);
    if (!pt) {
      pt = { key, label, deliveries: 0, spots: 0, campaigns: 0 };
      buckets.set(key, pt);
    }
    pt[field]++;
  };

  for (const r of rows) {
    for (const ts of r.deliveryTimestamps) add(ts, 'deliveries');
    for (const ts of r.spotTimestamps) add(ts, 'spots');
    // One campaign counted at its delivery date.
    if (r.deliveryDate != null) add(r.deliveryDate, 'campaigns');
  }

  return Array.from(buckets.values()).sort((a, b) => (a.key < b.key ? -1 : 1));
}

// ---------------------------------------------------------------------------
// Aggregation
// ---------------------------------------------------------------------------

function summarizeSection(relationship: Relationship, rows: CampaignRow[]): SectionSummary {
  const s: SectionSummary = {
    relationship,
    label: RELATIONSHIP_LABELS[relationship],
    campaigns: rows.length,
    stations: 0, spots: 0, tracks: 0, liveReads: 0,
    deliveryRecords: 0, accepted: 0, rejected: 0, pending: 0,
    downloadsComplete: 0, downloadsTotal: 0, ftpSends: 0,
    qcPass: 0, qcFail: 0, qcPendingCount: 0,
    approved: 0, reviewRejected: 0, reviewPending: 0,
    qcTestsTotal: 0, qcFailuresTotal: 0, reviewsTotal: 0, reviewRejectionsTotal: 0,
    rows,
  };
  for (const r of rows) {
    s.stations += r.stationCount;
    s.spots += r.spotCount;
    s.tracks += r.trackCount;
    s.liveReads += r.liveReadCount;
    s.deliveryRecords += r.deliveryRecords;
    s.accepted += r.accepted;
    s.rejected += r.rejected;
    s.pending += r.pending;
    s.downloadsComplete += r.downloadsComplete;
    s.downloadsTotal += r.downloadsTotal;
    s.ftpSends += r.ftpSends;
    s.qcPass += r.qcPass;
    s.qcFail += r.qcFail;
    s.qcPendingCount += r.qcPendingCount;
    s.approved += r.approved;
    s.reviewRejected += r.reviewRejected;
    s.reviewPending += r.reviewPending;
    s.qcTestsTotal += r.qcTestsTotal || 0;
    s.qcFailuresTotal += r.qcFailuresTotal || 0;
    s.reviewsTotal += r.reviewsTotal || 0;
    s.reviewRejectionsTotal += r.reviewRejectionsTotal || 0;
  }
  return s;
}

const topN = (map: Record<string, number>, n = 12): { name: string; value: number }[] =>
  Object.entries(map)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n)
    .map(([name, value]) => ({ name, value }));

function buildCharts(
  rows: CampaignRow[],
  sections: SectionSummary[],
  range: DateRange,
  broadcasters: BroadcasterStat[],
): CampaignReport['charts'] {
  const languages: Record<string, number> = {};
  const durations: Record<string, number> = {};
  const perClient: Record<string, number> = {};
  const downloadsClient: Record<string, { complete: number; outstanding: number }> = {};

  for (const r of rows) {
    for (const [k, v] of Object.entries(r.languages)) bump(languages, k, v);
    for (const [k, v] of Object.entries(r.durations)) bump(durations, k, v);
    bump(perClient, r.client || 'Unspecified');
    const c = r.client || 'Unspecified';
    if (!downloadsClient[c]) downloadsClient[c] = { complete: 0, outstanding: 0 };
    downloadsClient[c].complete += r.downloadsComplete;
    downloadsClient[c].outstanding += Math.max(0, r.downloadsTotal - r.downloadsComplete);
  }

  const relationshipSplit = sections.map((s) => ({ name: s.label, value: s.campaigns }));

  // Delivery status — mutually-exclusive delivery RESPONSES (one per
  // delivery record). Downloads are NOT a status here: a delivery can be
  // both accepted AND downloaded, so mixing them double-counts. Download
  // engagement is its own chart below.
  const totAccepted = sections.reduce((a, s) => a + s.accepted, 0);
  const totRejected = sections.reduce((a, s) => a + s.rejected, 0);
  const totPending = sections.reduce((a, s) => a + s.pending, 0);
  // Auto-copy model: a delivery lands in the broadcaster's library on
  // Deliver, and only an explicit Reject removes it. So 'delivered' and
  // 'accepted' are the same successful outcome — we fold them into a single
  // "Delivered". Only "Rejected" is a distinct state.
  const deliveryStatusSplit = [
    { name: 'Delivered', value: totPending + totAccepted },
    { name: 'Rejected', value: totRejected },
  ].filter((d) => d.value > 0);

  // Download engagement — mutually-exclusive: of everything delivered, how
  // much has been pulled vs still outstanding. Meaningful for every section
  // (studio/agency/broadcaster copies all carry synced download counts).
  const totDownloaded = sections.reduce((a, s) => a + s.downloadsComplete, 0);
  const totDownloadTarget = sections.reduce((a, s) => a + s.downloadsTotal, 0);
  const downloadEngagement = [
    { name: 'Downloaded', value: totDownloaded },
    { name: 'Outstanding', value: Math.max(0, totDownloadTarget - totDownloaded) },
  ].filter((d) => d.value > 0);

  const qcPass = sections.reduce((a, s) => a + s.qcPass, 0);
  const qcFail = sections.reduce((a, s) => a + s.qcFail, 0);
  const qcPend = sections.reduce((a, s) => a + s.qcPendingCount, 0);
  const qcSplit = [
    { name: 'Pass', value: qcPass },
    { name: 'Fail', value: qcFail },
    { name: 'Not tested', value: qcPend },
  ].filter((d) => d.value > 0);

  const approved = sections.reduce((a, s) => a + s.approved, 0);
  const revRej = sections.reduce((a, s) => a + s.reviewRejected, 0);
  const revPend = sections.reduce((a, s) => a + s.reviewPending, 0);
  const reviewSplit = [
    { name: 'Approved', value: approved },
    { name: 'Rejected', value: revRej },
    { name: 'Pending', value: revPend },
  ].filter((d) => d.value > 0);

  return {
    relationshipSplit: relationshipSplit.filter((d) => d.value > 0),
    deliveryStatusSplit,
    downloadEngagement,
    languageSpread: topN(languages),
    durationSpread: topN(durations),
    campaignsPerClient: topN(perClient),
    downloadsByClient: Object.entries(downloadsClient)
      .sort((a, b) => (b[1].complete + b[1].outstanding) - (a[1].complete + a[1].outstanding))
      .slice(0, 12)
      .map(([name, v]) => ({ name, complete: v.complete, outstanding: v.outstanding })),
    qcSplit,
    reviewSplit,
    downloadsByBroadcaster: broadcasters
      .slice(0, 15)
      .map((b) => ({ name: b.name, complete: b.downloaded, outstanding: b.partial + b.outstanding })),
    trend: buildTrend(rows, range),
  };
}

function buildHeadline(sections: SectionSummary[]): CampaignReport['headline'] {
  const sum = (f: (s: SectionSummary) => number) => sections.reduce((a, s) => a + f(s), 0);
  const totalDownloads = sum((s) => s.downloadsTotal);
  const downloadsComplete = sum((s) => s.downloadsComplete);
  const accepted = sum((s) => s.accepted);
  const rejected = sum((s) => s.rejected);
  const pending = sum((s) => s.pending);
  const qcPass = sum((s) => s.qcPass);
  const qcFail = sum((s) => s.qcFail);
  const approved = sum((s) => s.approved);
  const reviewRejected = sum((s) => s.reviewRejected);

  return {
    totalCampaigns: sum((s) => s.campaigns),
    totalStations: sum((s) => s.stations),
    totalSpots: sum((s) => s.spots),
    totalTracks: sum((s) => s.tracks),
    totalLiveReads: sum((s) => s.liveReads),
    totalDeliveryRecords: sum((s) => s.deliveryRecords),
    downloadsComplete,
    downloadsTotal: totalDownloads,
    downloadRate: totalDownloads > 0 ? downloadsComplete / totalDownloads : 0,
    accepted,
    rejected,
    pending,
    acceptRate: accepted + rejected > 0 ? accepted / (accepted + rejected) : 0,
    qcPass,
    qcFail,
    qcFailRate: qcPass + qcFail > 0 ? qcFail / (qcPass + qcFail) : 0,
    approved,
    reviewRejected,
    reviewRejectRate: approved + reviewRejected > 0 ? reviewRejected / (approved + reviewRejected) : 0,
    ftpSends: sum((s) => s.ftpSends),
  };
}

// ---------------------------------------------------------------------------
// Public: assemble the report
// ---------------------------------------------------------------------------

interface BuildArgs {
  firebase: any;
  orgId: string;
  orgName: string;
  tracksById: Record<string, any>;
  filters: CampaignReportFilters;
}

/**
 * Fetch + assemble the cross-campaign report for the current org.
 * Reads the org's jobs collection; for SENT campaigns also reads each
 * job's deliveries subcollection. Optionally deep-reads qcRecords / reviews.
 */
/**
 * Distinct client names across the org's campaigns — for populating the
 * Campaign Reports client filter BEFORE a report is generated. Reads only the
 * jobs collection (no deliveries), so it's a single cheap read.
 */
export async function fetchOrgClients(firebase: any, orgId: string): Promise<string[]> {
  try {
    const snap = await getDocs(collection(firebase.db, getOrgCollectionPath(orgId, 'jobs')));
    const set = new Set<string>();
    snap.docs.forEach((d: any) => {
      const c = clientName(d.data()?.client);
      if (c) set.add(c);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  } catch {
    return [];
  }
}

export async function buildCampaignReport({
  firebase,
  orgId,
  orgName,
  tracksById,
  filters,
}: BuildArgs): Promise<CampaignReport> {
  const jobsSnap = await getDocs(query(collection(firebase.db, getOrgCollectionPath(orgId, 'jobs'))));
  const jobs = jobsSnap.docs.map((d: any) => ({ id: d.id, ...d.data() }));

  // Distinct client names across ALL campaigns (for the filter UI) — computed
  // before date/client filtering so the picker always shows every option.
  const availableClients = Array.from(
    new Set(jobs.map((j: any) => clientName(j.client)).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b));

  const clientFilterSet = new Set(filters.clients.map((c) => c.toLowerCase()));
  const clientOk = (j: any) =>
    clientFilterSet.size === 0 || clientFilterSet.has(clientName(j.client).toLowerCase());

  // Compute rows. For sent campaigns fetch deliveries (needed for accurate
  // metrics + date anchor); copies use job-level fields.
  const rows: CampaignRow[] = [];

  // Fetch deliveries for sent campaigns in parallel (bounded implicitly by
  // campaign count — a manual, admin-triggered report).
  const sentJobs = jobs.filter((j: any) => relationshipOf(j) === 'sent' && clientOk(j));
  const deliveriesByJob = new Map<string, any[]>();
  await Promise.all(
    sentJobs.map(async (j: any) => {
      try {
        const dSnap = await getDocs(
          collection(firebase.db, getOrgCollectionPath(orgId, 'jobs'), j.id, 'deliveries'),
        );
        deliveriesByJob.set(j.id, dSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
      } catch {
        deliveriesByJob.set(j.id, []);
      }
    }),
  );

  // For COPY campaigns (shared/delivered), the per-broadcaster download map
  // (broadcasterDownloadStatus) is typically EMPTY on the copy — the download
  // CF syncs the aggregate counter to copies but not the map. The
  // authoritative map lives on the SOURCE job doc, which Firestore rules
  // allow any authenticated user to read. Fetch it so a copy's report mirrors
  // the source's per-broadcaster breakdown. Falls back to the copy's own
  // (possibly empty) fields if the source is missing/unreadable.
  const copyJobs = jobs.filter(
    (j: any) => relationshipOf(j) !== 'sent' && j.sourceOrgId && j.sourceJobId && clientOk(j),
  );
  const sourceDownloadByJobId = new Map<string, { deliveryCount: any; downloadedDeliveryCount: any; broadcasterDownloadStatus: Record<string, string> }>();
  // Accept/reject/FTP live only on the source's deliveries sub-collection —
  // copies don't hold their own. Rules allow reading /jobs/{id}/deliveries,
  // so fetch the source's for copies to populate those metrics (mirrors the
  // source's numbers). Fetched together with the source job doc.
  const sourceDeliveriesByJobId = new Map<string, any[]>();
  await Promise.all(
    copyJobs.map(async (j: any) => {
      await Promise.all([
        (async () => {
          try {
            const srcSnap = await getDoc(
              doc(firebase.db, getOrgCollectionPath(j.sourceOrgId, 'jobs'), j.sourceJobId),
            );
            if (srcSnap.exists()) {
              const sd: any = srcSnap.data();
              sourceDownloadByJobId.set(j.id, {
                deliveryCount: sd.deliveryCount,
                downloadedDeliveryCount: sd.downloadedDeliveryCount,
                broadcasterDownloadStatus: sd.broadcasterDownloadStatus || {},
              });
            }
          } catch { /* fall back to the copy's own fields */ }
        })(),
        (async () => {
          try {
            const dSnap = await getDocs(
              collection(firebase.db, getOrgCollectionPath(j.sourceOrgId, 'jobs'), j.sourceJobId, 'deliveries'),
            );
            sourceDeliveriesByJobId.set(j.id, dSnap.docs.map((d: any) => ({ id: d.id, ...d.data() })));
          } catch { /* accept/reject/ftp just stay 0 for this copy */ }
        })(),
      ]);
    }),
  );

  for (const job of jobs) {
    if (!clientOk(job)) continue;
    const relationship = relationshipOf(job);
    const deliveries = relationship === 'sent' ? deliveriesByJob.get(job.id) || [] : null;
    const row = computeCampaignRow({
      job, deliveries, tracksById, orgId,
      downloadOverride: sourceDownloadByJobId.get(job.id),
      sourceDeliveries: sourceDeliveriesByJobId.get(job.id),
    });
    // Date filter on the delivery-date anchor.
    if ((filters.dateRange.start != null || filters.dateRange.end != null) && !inRange(row.deliveryDate, filters.dateRange)) {
      continue;
    }
    // A sent campaign with no deliveries at all isn't "sent" — skip it.
    if (relationship === 'sent' && row.deliveryRecords === 0) continue;
    rows.push(row);
  }

  // Optional deep QC / review history.
  if (filters.includeQcReviewHistory) {
    await enrichWithHistory({ firebase, orgId, rows, tracksById });
  }

  // Bucket into sections (only non-empty ones).
  const order: Relationship[] = ['sent', 'shared', 'delivered'];
  const sections = order
    .map((rel) => summarizeSection(rel, rows.filter((r) => r.relationship === rel)))
    .filter((s) => s.campaigns > 0);

  const broadcasters = buildBroadcasterBreakdown(rows);

  return {
    orgName,
    filters,
    generatedAt: Date.now(),
    sections,
    headline: buildHeadline(sections),
    charts: buildCharts(rows, sections, filters.dateRange, broadcasters),
    broadcasters,
    availableClients,
  };
}

// ---------------------------------------------------------------------------
// Deep QC / review history (optional)
// ---------------------------------------------------------------------------

interface EnrichArgs {
  firebase: any;
  orgId: string;
  rows: CampaignRow[];
  tracksById: Record<string, any>;
}

interface TrackHistory {
  qcTests: number;
  qcFailures: number;
  reviews: number;
  reviewRejections: number;
  rejectionReasons: string[];
}

/**
 * For each row, read qcRecords + reviews for its linked tracks and stamp
 * historical totals. QC / review history is authored on the org that owns
 * the audio (source for copies — carried on the row as `historyOrgId`).
 * De-duplicated per (readOrgId, trackId) so a track referenced by several
 * campaigns is only read once.
 */
async function enrichWithHistory({ firebase, rows, tracksById }: EnrichArgs): Promise<void> {
  const { collection: fbCollection, getDocs: fbGetDocs } = await import('firebase/firestore');

  // Unique (readOrgId, trackId) pairs across all rows.
  const pairs = new Map<string, { readOrgId: string; trackId: string }>();
  for (const r of rows) {
    for (const tid of r.linkedTrackIds) {
      const readOrgId = r.historyOrgId || '';
      if (!readOrgId || !tid) continue;
      pairs.set(`${readOrgId}::${tid}`, { readOrgId, trackId: tid });
    }
  }

  const histByKey = new Map<string, TrackHistory>();
  await Promise.all(
    Array.from(pairs.values()).map(async ({ readOrgId, trackId }) => {
      const h: TrackHistory = { qcTests: 0, qcFailures: 0, reviews: 0, reviewRejections: 0, rejectionReasons: [] };
      try {
        const qcSnap = await fbGetDocs(
          fbCollection(firebase.db, getOrgCollectionPath(readOrgId, 'audiometa'), trackId, 'qcRecords'),
        );
        qcSnap.forEach((d: any) => {
          const data = d.data();
          if (data.status === 'testing') return; // in-flight, not a completed test
          h.qcTests++;
          if (data.status === 'fail') h.qcFailures++;
        });
      } catch { /* rules / missing — leave zeros */ }
      try {
        const rvSnap = await fbGetDocs(
          fbCollection(firebase.db, getOrgCollectionPath(readOrgId, 'audiometa'), trackId, 'reviews'),
        );
        rvSnap.forEach((d: any) => {
          const data = d.data();
          if (data.status === 'recalled') return;
          h.reviews++;
          if (data.status === 'rejected') {
            h.reviewRejections++;
            if (data.rejectionReason) h.rejectionReasons.push(String(data.rejectionReason));
          }
        });
      } catch { /* rules / missing — leave zeros */ }
      histByKey.set(`${readOrgId}::${trackId}`, h);
    }),
  );
  void tracksById;

  // Roll the per-track history up onto each row.
  for (const r of rows) {
    let qcTests = 0, qcFailures = 0, reviews = 0, reviewRejections = 0;
    const reasons: string[] = [];
    for (const tid of r.linkedTrackIds) {
      const h = histByKey.get(`${r.historyOrgId}::${tid}`);
      if (!h) continue;
      qcTests += h.qcTests;
      qcFailures += h.qcFailures;
      reviews += h.reviews;
      reviewRejections += h.reviewRejections;
      reasons.push(...h.rejectionReasons);
    }
    r.qcTestsTotal = qcTests;
    r.qcFailuresTotal = qcFailures;
    r.reviewsTotal = reviews;
    r.reviewRejectionsTotal = reviewRejections;
    r.rejectionReasons = reasons;
  }
}

// ===========================================================================
// Platform report — cross-org, superadmin / master-org only
// ===========================================================================
//
// Aggregates the SAME metrics as the single-org campaign report, but across
// every organization on the platform. To avoid double-counting, it uses only
// SOURCE campaigns (relationship === 'sent') — the org that created the
// campaign owns the one authoritative copy; delivered/shared copies in other
// orgs are ignored. A campaign's deliveries + linked tracks live in that same
// source org, so downloads / QC / review resolve locally per org.

export interface PlatformReportFilters {
  dateRange: DateRange;
  /** Selected org ids. Empty = all orgs. */
  orgIds: string[];
  /** Selected client names (case-insensitive). Empty = all clients. */
  clients: string[];
  includeQcReviewHistory: boolean;
}

/** Per-source-org rollup for the platform breakdown. */
export interface OrgStat {
  orgId: string;
  orgName: string;
  campaigns: number;
  spots: number;
  placements: number;
  deliveries: number;
  downloadsComplete: number;
  downloadsTotal: number;
  downloadRate: number;
  rejected: number;
  qcPass: number;
  qcFail: number;
}

export interface PlatformReport {
  filters: PlatformReportFilters;
  generatedAt: number;
  /** Number of source orgs that contributed at least one campaign. */
  orgCount: number;
  headline: CampaignReport['headline'];
  charts: CampaignReport['charts'] & {
    campaignsPerOrg: { name: string; value: number }[];
    downloadsByOrg: { name: string; complete: number; outstanding: number }[];
  };
  broadcasters: BroadcasterStat[];
  /** Per-source-org breakdown, most campaigns first. */
  orgs: OrgStat[];
  /** Every source campaign (row.sourceOrgName carries the owning org). */
  rows: CampaignRow[];
  availableClients: string[];
  availableOrgs: { id: string; name: string }[];
}

interface BuildPlatformArgs {
  firebase: any;
  filters: PlatformReportFilters;
}

// Shared per-org bucket produced by both fetch strategies.
type PerOrg = { org: { id: string; name: string }; rows: CampaignRow[]; clients: string[] };

/**
 * Build a CampaignRow for one SOURCE campaign, applying the date filter.
 * Returns null when the campaign should be excluded. QC / review state comes
 * from the audio snapshots embedded in the delivery records (each delivered
 * station carries qc_status / approval_status for its linked track), so we
 * never read the org's full audiometa collection — the platform report's
 * biggest cost. Snapshots are delivery-time point-in-time, fine for a
 * cross-org overview.
 */
function platformRowFor(job: any, deliveries: any[], orgId: string, orgName: string, filters: PlatformReportFilters): CampaignRow | null {
  const trackMeta: Record<string, any> = {};
  for (const d of deliveries) {
    const st = Array.isArray(d.stations) ? d.stations : [];
    for (const s of st) {
      if (s && s.linkedTrackId && !trackMeta[s.linkedTrackId]) {
        trackMeta[s.linkedTrackId] = { qc_status: s.qc_status, approval_status: s.approval_status };
      }
    }
  }
  const row = computeCampaignRow({ job, deliveries, tracksById: trackMeta, orgId });
  if ((filters.dateRange.start != null || filters.dateRange.end != null) && !inRange(row.deliveryDate, filters.dateRange)) return null;
  if (row.deliveryRecords === 0) return null; // never actually delivered
  row.sourceOrgName = orgName;
  (row as any).ownerOrgId = orgId;
  return row;
}

/**
 * FAST PATH — one collectionGroup query for ALL jobs + one for ALL deliveries
 * across every org (~2 round-trips instead of hundreds). Permitted only for
 * superadmins (see firestore.rules `{path=**}/jobs` + `{path=**}/deliveries`);
 * for anyone else the query is permission-denied and the caller falls back to
 * the per-org path. Filtering (source-only, org, client, date) is done in
 * memory, so the metrics are byte-for-byte identical to the per-org path.
 */
async function fetchPlatformCollectionGroup(
  firebase: any,
  orgNameById: Record<string, string>,
  orgFilter: Set<string>,
  clientOk: (j: any) => boolean,
  filters: PlatformReportFilters,
): Promise<PerOrg[]> {
  // Deliveries only exist under source jobs, so grouping by parent job id
  // gives each source campaign its delivery set.
  const delSnap = await getDocs(collectionGroup(firebase.db, 'deliveries'));
  const deliveriesByJob = new Map<string, any[]>();
  delSnap.docs.forEach((d: any) => {
    const jobId = d.ref?.parent?.parent?.id; // .../jobs/{jobId}/deliveries/{id}
    if (!jobId) return;
    const arr = deliveriesByJob.get(jobId) || [];
    arr.push({ id: d.id, ...d.data() });
    deliveriesByJob.set(jobId, arr);
  });

  const jobsSnap = await getDocs(collectionGroup(firebase.db, 'jobs'));
  const byOrg = new Map<string, PerOrg>();
  jobsSnap.docs.forEach((jd: any) => {
    const orgId = jd.ref?.parent?.parent?.id; // organizations/{orgId}/jobs/{jobId}
    if (!orgId || !(orgId in orgNameById)) return; // deleted / unknown org
    if (orgFilter.size && !orgFilter.has(orgId)) return;
    const job = { id: jd.id, ...jd.data() };
    if (relationshipOf(job) !== 'sent') return; // source campaigns only
    let entry = byOrg.get(orgId);
    if (!entry) { entry = { org: { id: orgId, name: orgNameById[orgId] }, rows: [], clients: [] }; byOrg.set(orgId, entry); }
    entry.clients.push(clientName(job.client)); // picker options — before client filter
    if (!clientOk(job)) return;
    const row = platformRowFor(job, deliveriesByJob.get(job.id) || [], orgId, orgNameById[orgId], filters);
    if (row) entry.rows.push(row);
  });
  return Array.from(byOrg.values());
}

/**
 * FALLBACK PATH — iterate each org's jobs + deliveries with per-doc reads
 * (existing `isAuthenticated` rules). Slower, but works for master-org admins
 * who aren't superadmins and therefore can't run the collectionGroup query.
 */
async function fetchPlatformPerOrg(
  firebase: any,
  targetOrgs: any[],
  clientOk: (j: any) => boolean,
  filters: PlatformReportFilters,
): Promise<PerOrg[]> {
  return Promise.all(
    targetOrgs.map(async (org: any): Promise<PerOrg> => {
      const result: PerOrg = { org: { id: org.id, name: org.name }, rows: [], clients: [] };
      let jobs: any[] = [];
      try {
        const snap = await getDocs(collection(firebase.db, getOrgCollectionPath(org.id, 'jobs')));
        jobs = snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
      } catch {
        return result; // org unreadable — skip
      }
      const sourceJobs = jobs.filter((j) => relationshipOf(j) === 'sent');
      result.clients = sourceJobs.map((j) => clientName(j.client)).filter(Boolean);
      const eligible = sourceJobs.filter(clientOk);
      await Promise.all(
        eligible.map(async (job) => {
          let deliveries: any[] = [];
          try {
            const dSnap = await getDocs(
              collection(firebase.db, getOrgCollectionPath(org.id, 'jobs'), job.id, 'deliveries'),
            );
            deliveries = dSnap.docs.map((d: any) => ({ id: d.id, ...d.data() }));
          } catch { /* no deliveries readable */ }
          const row = platformRowFor(job, deliveries, org.id, org.name, filters);
          if (row) result.rows.push(row);
        }),
      );
      return result;
    }),
  );
}

export async function buildPlatformReport({ firebase, filters }: BuildPlatformArgs): Promise<PlatformReport> {
  const orgsSnap = await firebase.getAllOrganizations();
  const allOrgs: any[] = orgsSnap.docs
    .map((d: any) => ({ id: d.id, name: d.data()?.name || d.id, ...d.data() }))
    // Skip soft-deleted orgs so the platform view reflects live orgs only.
    .filter((o: any) => !o.deletedAt && o.status !== 'deleted');

  const availableOrgs: { id: string; name: string }[] = allOrgs
    .map((o: any) => ({ id: o.id, name: o.name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const orgNameById: Record<string, string> = Object.fromEntries(allOrgs.map((o: any) => [o.id, o.name]));

  const orgFilter = new Set(filters.orgIds);
  const targetOrgs: any[] = orgFilter.size ? allOrgs.filter((o: any) => orgFilter.has(o.id)) : allOrgs;

  const clientFilterSet = new Set(filters.clients.map((c) => c.toLowerCase()));
  const clientOk = (j: any) =>
    clientFilterSet.size === 0 || clientFilterSet.has(clientName(j.client).toLowerCase());

  // Superadmins get the single-query collectionGroup fast path; master-org
  // admins (no collectionGroup access) fall back to per-org iteration. Both
  // filter in memory, so the resulting metrics are identical.
  let perOrg: PerOrg[];
  try {
    perOrg = await fetchPlatformCollectionGroup(firebase, orgNameById, orgFilter, clientOk, filters);
  } catch (err: any) {
    if (err?.code === 'permission-denied' || /permission/i.test(err?.message || '')) {
      perOrg = await fetchPlatformPerOrg(firebase, targetOrgs, clientOk, filters);
    } else {
      throw err;
    }
  }

  const rows = perOrg.flatMap((p) => p.rows);
  const availableClients = Array.from(new Set(perOrg.flatMap((p) => p.clients).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b));

  if (filters.includeQcReviewHistory) {
    // enrichWithHistory reads each row's historyOrgId (= the owning org for
    // source campaigns), so it works across orgs unchanged.
    await enrichWithHistory({ firebase, orgId: '', rows, tracksById: {} });
  }

  // Single aggregate "section" (all source campaigns) drives headline + charts.
  const section = summarizeSection('sent', rows);
  const broadcasters = buildBroadcasterBreakdown(rows);
  const baseCharts = buildCharts(rows, [section], filters.dateRange, broadcasters);

  // Per-org breakdown.
  const orgs: OrgStat[] = perOrg
    .filter((p) => p.rows.length > 0)
    .map((p) => {
      const r = p.rows;
      const sum = (f: (x: CampaignRow) => number) => r.reduce((a, x) => a + f(x), 0);
      const dTotal = sum((x) => x.downloadsTotal);
      const dComplete = sum((x) => x.downloadsComplete);
      return {
        orgId: p.org.id,
        orgName: p.org.name,
        campaigns: r.length,
        spots: sum((x) => x.spotCount),
        placements: sum((x) => x.stationCount),
        deliveries: sum((x) => x.deliveryRecords),
        downloadsComplete: dComplete,
        downloadsTotal: dTotal,
        downloadRate: dTotal > 0 ? dComplete / dTotal : 0,
        rejected: sum((x) => x.rejected),
        qcPass: sum((x) => x.qcPass),
        qcFail: sum((x) => x.qcFail),
      };
    })
    .sort((a, b) => (b.campaigns - a.campaigns) || a.orgName.localeCompare(b.orgName));

  const charts = {
    ...baseCharts,
    campaignsPerOrg: orgs.slice(0, 15).map((o) => ({ name: o.orgName, value: o.campaigns })),
    downloadsByOrg: orgs
      .slice(0, 15)
      .map((o) => ({ name: o.orgName, complete: o.downloadsComplete, outstanding: Math.max(0, o.downloadsTotal - o.downloadsComplete) })),
  };

  return {
    filters,
    generatedAt: Date.now(),
    orgCount: orgs.length,
    headline: buildHeadline([section]),
    charts,
    broadcasters,
    orgs,
    rows,
    availableClients,
    availableOrgs,
  };
}
